import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity'
import { Action, createReducer, on } from '@ngrx/store'

import * as CategoriesActions from './categories.actions'
import { CategoriesEntity } from './categories.models'
import { environment } from 'apps/gb-front/src/environments/environment'

export const CATEGORIES_FEATURE_KEY = 'categories'

export interface State extends EntityState<CategoriesEntity> {
  selectedId?: string | number // which Categories record has been selected
  loaded: boolean // has the Categories list been loaded
  error?: {} | null // last known error (if any)
  count: number // number of total categories
  stale: number[] // stale category pages
}

export interface CategoriesPartialState {
  readonly [CATEGORIES_FEATURE_KEY]: State
}

export const categoriesAdapter: EntityAdapter<CategoriesEntity> = createEntityAdapter<CategoriesEntity>({
  selectId: (category: CategoriesEntity) => category.uuid,
})

export const initialState: State = categoriesAdapter.getInitialState({
  selectedId: '',
  error: '',
  count: 0,
  loaded: false,
  stale: [],
})

const categoriesReducer = createReducer(
  initialState,
  on(CategoriesActions.loadCategories, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),
  on(CategoriesActions.loadCategoriesSuccess, (state, { categories }) =>
    categoriesAdapter.addMany(categories, {
      ...state,
      loaded: true,
    }),
  ),
  on(CategoriesActions.loadCategoriesFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(CategoriesActions.updateSelectedCategoryUuid, (state, { uuid }) => ({
    ...state,
    selectedId: uuid,
  })),
  on(CategoriesActions.updateCount, (state, action) => ({
    ...state,
    count: action.count,
  })),
  on(CategoriesActions.updateCategory, (state, action) => categoriesAdapter.updateOne(action.update, state)),
  on(CategoriesActions.addCategory, (state, action) => categoriesAdapter.addOne(action.category, state)),
  on(CategoriesActions.deleteCategory, (state, action) => categoriesAdapter.removeOne(action.uuid, state)),
  on(CategoriesActions.setAllCategoriesStale, (state) =>
    categoriesAdapter.setAll(getCategoriesWithStaleItems([...selectAll(state)]), {
      ...state,
      stale: getPages(state.ids),
    }),
  ),
  on(CategoriesActions.resyncCategoriesSuccess, (state, action) => {
    const categories: CategoriesEntity[] = [...selectAll(state)]

    // replace block of categories with updated categories
    categories.splice(action.offset, environment.categoriesPageSize, ...action.categories)

    return categoriesAdapter.setAll(categories, state)
  }),
  on(CategoriesActions.removePageFromStale, (state, action) => ({
    ...state,
    // remove resynced page from stale pages
    stale: state.stale.filter((page) => page !== action.page),
  })),
  on(CategoriesActions.resyncCategoriesError, (state, error) => ({
    ...state,
    error,
  })),
)

export const getPages = (arr: string[] | number[]) => {
  return arr
    .map((_, index: number) => (index % environment.categoriesPageSize === 0 ? index : null))
    .filter((i): i is number => i !== null)
}

export const getCategoriesWithStaleItems = (categories: CategoriesEntity[]) =>
  categories.map((category) => ({
    ...category,
    items_preview: category.items_preview.map((item) => ({ ...item, isStale: true })),
  }))

export function reducer(state: State | undefined, action: Action) {
  return categoriesReducer(state, action)
}

export const { selectIds, selectEntities, selectAll, selectTotal } = categoriesAdapter.getSelectors()
